import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getSurvey, updateSurvey } from "../../utils/APIConnector";
import { renumberQuestions } from "../../utils/surveySchema";
import { localDateFromISO, localTimeFromISO } from "./utils/dateTime";
import { syncStructureOnLanguageSwitch } from "./utils/languageUtils";
import { prepareSurveyForPersist, downloadJsonObject } from "./utils/surveyPersistence";
import { normalizeMetricFlags } from "./utils/normalization";
import { normalizeSurveyDropdownOptions } from "./utils/surveyPersistence";
import {
    autoSyncSurveyStructure,
    initializeLanguageStructure,
    removeLanguageFromSurvey,
    getActiveLanguages
} from "./syncUtils";
import {
    createHandleChange,
    createPublishDateOnChange,
    createPublishTimeOnChange,
    createEndDateOnChange,
    createEndTimeOnChange,
    createHandleSave,
    createHandleDownload,
    createHandleFileChange,
    createHandleUploadClick,
} from "./utils/uiHandlers";
import styles from "./EditSurvey.module.css";
import QuestionsEditor from "./QuestionsEditor/QuestionsEditor";
import { createOnAddPage, createOnAddQuestion, createOnRemovePage, createOnRemoveQuestion, createOnMoveWithinPage, createOnUpdateQuestion, createOnInsertPageAt, createOnMovePageToPosition } from "./utils/editOperations";
import { generateTxtForSurveyLanguage, applyTxtTranslationToSurvey } from "./utils/translationIO";


export default function EditSurvey() {
    const LANGS = ['pl', 'en', 'de', 'es', 'zh', 'ja'];
    const { id } = useParams();
    const [survey, setSurveyRaw] = useState(null);
    const [error, setError] = useState(false);
    const { i18n, t } = useTranslation();
    const fileInputRef = useRef(null);
    const uploadTxtRef = useRef(null);
    // Local strings to allow manual editing without native pickers
    const [publishDateStr, setPublishDateStr] = useState("");
    const [publishTimeStr, setPublishTimeStr] = useState("");
    const [endDateStr, setEndDateStr] = useState("");
    const [endTimeStr, setEndTimeStr] = useState("");

    // Active languages state
    const [activeLanguages, setActiveLanguages] = useState(['pl']);

    // Translation editor (TXT) state
    const [translationLang, setTranslationLang] = useState('');
    const [showTranslationEditor, setShowTranslationEditor] = useState(false);
    const [translationText, setTranslationText] = useState('');
    const [translationError, setTranslationError] = useState('');

    // Auto-sync wrapper for setSurvey
    const setSurvey = useCallback((updater) => {
        setSurveyRaw((prev) => {
            const updated = typeof updater === 'function' ? updater(prev) : updater;
            // Auto-sync only if we have survey and active languages
            if (updated && activeLanguages.length > 0) {
                return autoSyncSurveyStructure(updated, activeLanguages, 'pl');
            }
            return updated;
        });
    }, [activeLanguages]);

    useEffect(() => {
        getSurvey(id)
            .then((data) => {
                const prepared = normalizeSurveyDropdownOptions(normalizeMetricFlags({ ...data }, LANGS), LANGS);
                // Detect active languages from loaded survey
                const detectedLangs = getActiveLanguages(prepared, LANGS);
                if (detectedLangs.length > 0) {
                    setActiveLanguages(detectedLangs);
                }
                setSurveyRaw(prepared);
            })
            .catch(() => setError(true));
    }, [id]);

    // Keep local manual-edit strings in sync when survey changes
    useEffect(() => {
        if (!survey) return;
        setPublishDateStr(localDateFromISO(survey.publishAt));
        setPublishTimeStr(localTimeFromISO(survey.publishAt) || "");
        setEndDateStr(localDateFromISO(survey.endSurvey));
        setEndTimeStr(localTimeFromISO(survey.endSurvey) || "");
    }, [survey?.publishAt, survey?.endSurvey]);

    // Initialize translation language: prefer current UI language, else first non-main
    useEffect(() => {
        if (!translationLang && survey) {
            const preferred = activeLanguages.find((lng) => lng === i18n.language && lng !== 'pl');
            const fallback = activeLanguages.find((lng) => lng !== 'pl') || 'pl';
            setTranslationLang(preferred || fallback || activeLanguages[0]);
        }
    }, [survey?.id, activeLanguages]);

    // Language toggle handlers
    const handleToggleLanguage = useCallback((lng) => {
        if (lng === 'pl' || survey?.published) return; // Nie można wyłączyć polskiego ani edytować opublikowanej

        if (activeLanguages.includes(lng)) {
            // Wyłącz język - usuń go z ankiety
            setActiveLanguages(prev => prev.filter(l => l !== lng));
            setSurveyRaw(prev => removeLanguageFromSurvey(prev, lng, 'pl'));
        } else {
            // Włącz język - zainicjuj strukturę
            setActiveLanguages(prev => [...prev, lng]);
            setSurveyRaw(prev => initializeLanguageStructure(prev, lng, 'pl'));
        }
    }, [activeLanguages, survey?.published]);

    const handleChange = createHandleChange(setSurvey, { language: 'pl' });

    const handleSave = createHandleSave({ getSurvey: () => survey, id, LANGS: activeLanguages, t, updateSurvey });

    const handleDownload = createHandleDownload({ getSurvey: () => survey, id, LANGS: activeLanguages, t });

    const handleUploadClick = createHandleUploadClick(fileInputRef);

    // Language toggles removed — languages are fixed; Polish is always the main language

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const text = reader.result?.toString() || '';
                const parsed = JSON.parse(text);
                // Detect active languages from uploaded JSON
                const detectedLangs = getActiveLanguages(parsed, LANGS);
                if (detectedLangs.length > 0) {
                    setActiveLanguages(detectedLangs);
                }
                setSurvey({ ...parsed });
                alert(t('survey.uploadSuccess', { defaultValue: 'Załadowano JSON ankiety.' }));
            } catch (err) {
                alert(t('survey.uploadError', { defaultValue: 'Nieprawidłowy plik JSON.' }) + `\n${err?.message || err}`);
            } finally {
                e.target.value = '';
            }
        };
        reader.onerror = () => {
            alert(t('survey.uploadError', { defaultValue: 'Nieprawidłowy plik JSON.' }));
            e.target.value = '';
        };
        reader.readAsText(file);
    };

    // Removed manual publish button; publishDate is controlled via initial info (publishAt)

    if (error) return <p>{t("survey.loadError")}</p>;
    if (!survey) return <p>{t("survey.loading")}</p>;

    return (
        <div className={styles.container}>
            <div>
                <h1 className={styles.title}>{t("createSurvey.title")}</h1>
            </div>


            <div className={styles.form}>
                <input ref={fileInputRef} type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={createHandleFileChange({ setSurvey, t })} />

                {/* Language selection */}
                <div className={styles.langContainer}>
                    <div className={styles.langTitle}>{t('editor.activeLanguages', { defaultValue: 'Aktywne języki' })}</div>
                    <div className={styles.langToggles}>
                        {LANGS.map((lng) => {
                            const isActive = activeLanguages.includes(lng);
                            const isPolish = lng === 'pl';
                            const isDisabled = isPolish || survey?.published;

                            return (
                                <label
                                    key={lng}
                                    className={`${styles.langToggle} ${isActive ? styles.langToggleActive : ''
                                        } ${isDisabled && !isPolish ? styles.langToggleDisabled : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isActive}
                                        onChange={() => handleToggleLanguage(lng)}
                                        disabled={isDisabled}
                                    />
                                    <span className={styles.langName}>{lng.toUpperCase()}</span>
                                    {isPolish && <span className={styles.langBadge}>({t('editor.primary', { defaultValue: 'główny' })})</span>}
                                </label>
                            );
                        })}
                    </div>
                    <div className={styles.langHelp}>
                        {t('editor.languageHelp', { defaultValue: 'Zaznacz języki, które mają być dostępne w ankiecie. Polski jest językiem głównym i nie może być wyłączony.' })}
                    </div>
                </div>

                {/* Tytuły i opisy dla wszystkich języków */}
                <div style={{ display: 'grid', gap: 12 }}>
                    <div style={{ fontWeight: 600 }}>{t('editor.titlesPerLanguage', { defaultValue: 'Tytuł i opis w każdym języku' })}</div>
                    {activeLanguages.map((lng) => (
                        <div key={lng} style={{ display: 'grid', gap: 6, border: '1px solid var(--color-text-secondary)', padding: 12, borderRadius: 8, background: 'var(--color-background-elements)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontWeight: 600, minWidth: 40 }}>{lng.toUpperCase()}</span>
                                <input
                                    type="text"
                                    name="title"
                                    value={survey.title?.[lng] || ""}
                                    onChange={(e) => handleChange({ target: { name: 'title', value: e.target.value }, lng })}
                                    className={styles.input}
                                    disabled={survey.published}
                                    placeholder={t('surveys.columns.title')}
                                />
                            </div>
                            <textarea
                                name="description"
                                value={survey.description?.[lng] || ""}
                                onChange={(e) => handleChange({ target: { name: 'description', value: e.target.value }, lng })}
                                className={styles.textarea}
                                disabled={survey.published}
                                placeholder={t('surveys.columns.description')}
                                rows={3}
                            />
                            {/* Tłumaczenie TXT: etykieta + przyciski w stylu projektu */}
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', marginTop: 4 }}>
                                <div style={{ marginRight: 'auto', fontWeight: 600, color: 'var(--color-text-primary)' }}>{t('editor.translation.label', { defaultValue: 'Tłumaczenie' })}</div>
                                <button type="button" className={styles.btn} onClick={() => {
                                    try {
                                        setTranslationLang(lng);
                                        setTranslationError('');
                                        setTranslationText(generateTxtForSurveyLanguage(survey, lng));
                                        setShowTranslationEditor(true);
                                    } catch (e) {
                                        alert((t('editor.translation.generateError', { defaultValue: 'Nie udało się wygenerować TXT.' })) + `\n${e?.message || e}`);
                                    }
                                }}>{t('actions.edit', { defaultValue: 'Edytuj' })}</button>
                                <button type="button" className={styles.btn} onClick={() => { setTranslationLang(lng); uploadTxtRef.current?.click(); }}>{t('actions.addFile', { defaultValue: 'Dodaj plik' })}</button>
                                <button type="button" className={styles.btn} onClick={() => {
                                    try {
                                        const txt = generateTxtForSurveyLanguage(survey, lng);
                                        const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `survey_${id}_${lng}.txt`;
                                        document.body.appendChild(a);
                                        a.click();
                                        document.body.removeChild(a);
                                        URL.revokeObjectURL(url);
                                    } catch (e) {
                                        alert((t('editor.translation.downloadError', { defaultValue: 'Nie udało się pobrać TXT.' })) + `\n${e?.message || e}`);
                                    }
                                }}>{t('actions.download', { defaultValue: 'Pobierz' })}</button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Ukryty input do wgrywania TXT (sterowany z przycisków przy językach) */}
                <input ref={uploadTxtRef} type="file" accept="text/plain,.txt" style={{ display: 'none' }} onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const lang = translationLang; // ustawiane podczas kliknięcia przycisku
                    const reader = new FileReader();
                    reader.onload = () => {
                        try {
                            const txt = reader.result?.toString() || '';
                            // Ensure target language structure exists and matches main language structure
                            const mainQs = Array.isArray(survey?.['pl']?.questionnaire) ? survey['pl'].questionnaire : [];
                            const targetQs = Array.isArray(survey?.[lang]?.questionnaire) ? survey[lang].questionnaire : [];
                            let baseSurvey = survey;
                            if (mainQs.length && targetQs.length !== mainQs.length) {
                                const seedQs = mainQs.map((q) => ({
                                    ...q,
                                    details: q?.details ? { ...q.details, options: Array.isArray(q?.details?.options) ? [...q.details.options] : q?.details?.options } : q?.details,
                                }));
                                baseSurvey = {
                                    ...survey,
                                    [lang]: {
                                        ...(survey?.[lang] || {}),
                                        questionnaire: seedQs,
                                    },
                                };
                            }
                            const next = applyTxtTranslationToSurvey(baseSurvey, lang, txt);
                            setSurvey(next);
                            alert(t('editor.translation.uploadOk', { defaultValue: 'Wgrano tłumaczenie TXT.' }));
                        } catch (err) {
                            alert(t('editor.translation.uploadError', { defaultValue: 'Nieprawidłowy plik TXT.' }) + `\n${err?.message || err}`);
                        } finally {
                            e.target.value = '';
                        }
                    };
                    reader.onerror = () => { alert(t('editor.translation.uploadError', { defaultValue: 'Nieprawidłowy plik TXT.' })); e.target.value = ''; };
                    reader.readAsText(file);
                }} />

                {showTranslationEditor && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                        <div style={{ width: '90%', maxWidth: 800, background: 'var(--color-background-elements)', borderRadius: 10, border: '1px solid var(--color-text-secondary)', padding: 16, display: 'grid', gap: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--color-text-primary)' }}>{t('editor.translation.editorTitle', { defaultValue: 'Edytor tłumaczenia TXT' })} — {translationLang.toUpperCase()}</div>
                                <div style={{ flex: 1 }} />
                                <button type="button" className={styles.btn} onClick={() => setShowTranslationEditor(false)}>{t('actions.close', { defaultValue: 'Zamknij' })}</button>
                            </div>
                            {translationError && (
                                <div style={{ padding: 8, background: 'var(--color-background)', color: 'var(--color-text-primary)', borderRadius: 6, border: '1px solid var(--color-text-secondary)' }}>{translationError}</div>
                            )}
                            <textarea
                                className={styles.textarea}
                                rows={18}
                                value={translationText}
                                onChange={(e) => setTranslationText(e.target.value)}
                                style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', background: 'var(--color-background)', color: 'var(--color-text-primary)' }}
                            />
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <button type="button" className={styles.btn} onClick={() => setShowTranslationEditor(false)}>{t('actions.cancel', { defaultValue: 'Anuluj' })}</button>
                                <button type="button" className={styles.btn} onClick={() => {
                                    try {
                                        // Ensure target language structure exists before applying TXT
                                        const mainQs = Array.isArray(survey?.['pl']?.questionnaire) ? survey['pl'].questionnaire : [];
                                        const targetQs = Array.isArray(survey?.[translationLang]?.questionnaire) ? survey[translationLang].questionnaire : [];
                                        let baseSurvey = survey;
                                        if (mainQs.length && targetQs.length !== mainQs.length) {
                                            const seedQs = mainQs.map((q) => ({
                                                ...q,
                                                details: q?.details ? { ...q.details, options: Array.isArray(q?.details?.options) ? [...q.details.options] : q?.details?.options } : q?.details,
                                            }));
                                            baseSurvey = {
                                                ...survey,
                                                [translationLang]: {
                                                    ...(survey?.[translationLang] || {}),
                                                    questionnaire: seedQs,
                                                },
                                            };
                                        }
                                        const next = applyTxtTranslationToSurvey(baseSurvey, translationLang, translationText);
                                        setSurvey(next);
                                        setTranslationError('');
                                        setShowTranslationEditor(false);
                                    } catch (err) {
                                        setTranslationError(t('editor.translation.applyError', { defaultValue: 'Nieprawidłowy TXT: liczba pytań niezgodna lub format błędny.' }) + `\n${err?.message || err}`);
                                    }
                                }}>{t('actions.apply', { defaultValue: 'Zastosuj' })}</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Language verified field removed per requirements */}

                <label className={styles.checkbox}>
                    <input
                        type="checkbox"
                        name="status"
                        checked={!!survey.status}
                        onChange={handleChange}
                        disabled={survey.published}
                    />
                    <span>{t("survey.status", { defaultValue: "Status aktywny" })}</span>
                </label>

                <label>
                    {t("survey.restriction")}
                    <select
                        name="restrictions"
                        value={survey.restrictions || "none"}
                        onChange={handleChange}
                        className={styles.select}
                        disabled={survey.published}
                    >
                        <option value="none">{t("survey.restrictions.none")}</option>
                        <option value="registered">{t("survey.restrictions.login", { defaultValue: "Tylko zarejestrowani użytkownicy" })}</option>
                        <option value="invited">{t("survey.restrictions.invited", { defaultValue: "Tylko na zaproszenie" })}</option>
                    </select>
                </label>

                <div className={styles.dateTimeGroup}>
                    <label>
                        {t("survey.publishAt", { defaultValue: "Data otwarcia" })}
                        <input
                            type="text"
                            inputMode="numeric"
                            placeholder="YYYY-MM-DD"
                            name="publishAt"
                            value={publishDateStr}
                            onChange={createPublishDateOnChange({ setPublishDateStr, getPublishTimeStr: () => publishTimeStr, setSurvey })}
                            className={styles.input}
                            disabled={survey.published}
                        />
                    </label>

                    <label>
                        {t("survey.publishTime", { defaultValue: "Godzina otwarcia" })}
                        <input
                            type="text"
                            inputMode="numeric"
                            placeholder="HH:MM"
                            name="publishTime"
                            value={publishTimeStr}
                            onChange={createPublishTimeOnChange({ setPublishTimeStr, getPublishDateStr: () => publishDateStr, setSurvey })}
                            className={styles.input}
                            disabled={survey.published}
                        />
                    </label>
                </div>

                <div className={styles.dateTimeGroup}>
                    <label>
                        {t("survey.endSurvey", { defaultValue: "Data zakończenia" })}
                        <input
                            type="text"
                            inputMode="numeric"
                            placeholder="YYYY-MM-DD"
                            name="endSurveyDate"
                            value={endDateStr}
                            onChange={createEndDateOnChange({ setEndDateStr, getEndTimeStr: () => endTimeStr, setSurvey })}
                            className={styles.input}
                            disabled={survey.published}
                        />
                    </label>

                    <label>
                        {t("survey.endTime", { defaultValue: "Godzina zakończenia" })}
                        <input
                            type="text"
                            inputMode="numeric"
                            placeholder="HH:MM"
                            name="endSurveyTime"
                            value={endTimeStr}
                            onChange={createEndTimeOnChange({ setEndTimeStr, getEndDateStr: () => endDateStr, setSurvey })}
                            className={styles.input}
                            disabled={survey.published}
                        />
                    </label>
                </div>

                <label>
                    {t("survey.estimatedTime", { defaultValue: "Szacowany czas (minuty)" })}
                    <input
                        type="number"
                        min={0}
                        name="estimatedTime"
                        value={typeof survey.estimatedTime === 'number' ? survey.estimatedTime : (survey.estimatedTime || "")}
                        onChange={(e) =>
                            setSurvey((prev) => ({
                                ...prev,
                                estimatedTime: e.target.value === "" ? "" : Number(e.target.value),
                            }))
                        }
                        className={styles.input}
                        disabled={survey.published}
                    />
                </label>
            </div>

            {/* Questions editor only (side preview removed) */}
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: '100%', maxWidth: 1100 }}>
                    <h2 className={styles.title}>{t('editor.questions', { defaultValue: 'Pytania' })}</h2>
                    <QuestionsEditor
                        questions={survey?.['pl']?.questionnaire || []}
                        previewQuestions={survey?.[i18n.language]?.questionnaire || []}
                        onChange={(next) => setSurvey((prev) => ({
                            ...prev,
                            ['pl']: {
                                ...(prev?.['pl'] || {}),
                                questionnaire: next,
                            },
                        }))}
                        onUpdateQuestion={createOnUpdateQuestion(setSurvey, 'pl', activeLanguages)}
                        onAddPage={createOnAddPage(setSurvey, activeLanguages, 'pl')}
                        onAddQuestion={createOnAddQuestion(setSurvey, activeLanguages, 'pl')}
                        onRemovePage={createOnRemovePage(setSurvey, activeLanguages)}
                        onRemoveQuestion={createOnRemoveQuestion(setSurvey, 'pl', activeLanguages)}
                        onMoveWithinPage={createOnMoveWithinPage(setSurvey, 'pl', activeLanguages)}
                        onInsertPageAt={createOnInsertPageAt(setSurvey, activeLanguages, 'pl')}
                        onMovePageToPosition={createOnMovePageToPosition(setSurvey, activeLanguages)}
                    />
                </div>
            </div>

            <div className={styles.actions}>
                <button className={styles.btn} onClick={handleSave} disabled={survey.published}>
                    {t("actions.saveSurvey", { defaultValue: "Zapisz ankietę" })}
                </button>
                <button className={styles.btn} type="button" onClick={handleDownload}>
                    {t('actions.downloadJson', { defaultValue: 'Pobierz JSON' })}
                </button>
                <button className={styles.btn} type="button" onClick={handleUploadClick} disabled={survey.published}>
                    {t('actions.uploadJson', { defaultValue: 'Wgraj JSON' })}
                </button>
            </div>


        </div>
    );
}
